import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldAlert, AlertTriangle, Terminal, X, Check, Zap, HelpCircle } from 'lucide-react';

export interface NexoraXConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  impactLevel?: 'LOW' | 'MEDIUM' | 'HIGH';
  nodeName?: string;
}

declare global {
  interface Window {
    confirmNexoraXAction?: (
      options: NexoraXConfirmOptions,
      onConfirm: () => void,
      onCancel?: () => void
    ) => void;
  }
}

export function NexoraXConfirmationDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<NexoraXConfirmOptions | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<(() => void) | null>(null);
  const [pendingCancel, setPendingCancel] = useState<(() => void) | null>(null);

  // Sound generator matching futuristic UI feedback
  const playCyberSound = (type: 'open' | 'click' | 'warn') => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      if (type === 'warn') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(150, ctx.currentTime);
        osc.frequency.setValueAtTime(80, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.12, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc.start();
        osc.stop(ctx.currentTime + 0.4);
      } else if (type === 'open') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(520, ctx.currentTime);
        osc.frequency.setValueAtTime(840, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.06, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
        osc.start();
        osc.stop(ctx.currentTime + 0.25);
      } else {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        gain.gain.setValueAtTime(0.05, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        osc.start();
        osc.stop(ctx.currentTime + 0.15);
      }
    } catch (e) {
      // Ignore muted audio restrictions
    }
  };

  useEffect(() => {
    // Mount the global function for direct procedural components calling
    window.confirmNexoraXAction = (
      opts: NexoraXConfirmOptions,
      onConfirm: () => void,
      onCancel?: () => void
    ) => {
      setOptions(opts);
      setPendingConfirm(() => onConfirm);
      setPendingCancel(() => onCancel || null);
      setIsOpen(true);
      playCyberSound(opts.impactLevel === 'HIGH' ? 'warn' : 'open');
    };

    // Also support CustomEvent routing for decoupled layers
    const handleEventTrigger = (e: Event) => {
      const customEvent = e as CustomEvent<{
        id: string;
        options: NexoraXConfirmOptions;
      }>;
      if (!customEvent.detail) return;
      
      const { id, options: eventOpts } = customEvent.detail;
      
      window.confirmNexoraXAction?.(
        eventOpts,
        () => {
          window.dispatchEvent(
            new CustomEvent('NexoraX-confirm-dialog-resolved', {
              detail: { id, confirmed: true },
            })
          );
        },
        () => {
          window.dispatchEvent(
            new CustomEvent('NexoraX-confirm-dialog-resolved', {
              detail: { id, confirmed: false },
            })
          );
        }
      );
    };

    window.addEventListener('NexoraX-confirm-dialog-trigger', handleEventTrigger);

    return () => {
      delete window.confirmNexoraXAction;
      window.removeEventListener('NexoraX-confirm-dialog-trigger', handleEventTrigger);
    };
  }, []);

  const handleConfirm = () => {
    playCyberSound('click');
    setIsOpen(false);
    if (pendingConfirm) {
      pendingConfirm();
    }
    resetState();
  };

  const handleCancel = () => {
    playCyberSound('click');
    setIsOpen(false);
    if (pendingCancel) {
      pendingCancel();
    }
    resetState();
  };

  const resetState = () => {
    setOptions(null);
    setPendingConfirm(null);
    setPendingCancel(null);
  };

  if (!isOpen || !options) return null;

  const currentImpact = options.impactLevel || 'LOW';
  
  // Custom theme variables according to Nexorax absolute styling and corporate safety guidelines
  const theme = {
    HIGH: {
      border: 'border-rose-500/30',
      shadow: 'shadow-[0_0_50px_rgba(244,63,94,0.15)]',
      glow: 'text-rose-400',
      badgeBg: 'bg-rose-950/20 border-rose-500/20 text-rose-300',
      accentBar: 'bg-rose-500',
      bannerText: 'CRITICAL SECURITY Clearance DIRECTIVE',
      confirmBtn: 'bg-gradient-to-r from-rose-950 to-red-900 border-rose-500 text-rose-300 hover:from-rose-900 hover:to-red-800'
    },
    MEDIUM: {
      border: 'border-amber-500/30',
      shadow: 'shadow-[0_0_40px_rgba(245,158,11,0.12)]',
      glow: 'text-amber-400',
      badgeBg: 'bg-amber-950/20 border-amber-500/20 text-amber-300',
      accentBar: 'bg-amber-500',
      bannerText: 'ADMINISTRATIVE OVERRIDE VERIFICATION',
      confirmBtn: 'bg-gradient-to-r from-amber-950 to-yellow-900 border-amber-500 text-amber-300 hover:from-amber-900 hover:to-yellow-800'
    },
    LOW: {
      border: 'border-cyan-500/30',
      shadow: 'shadow-[0_0_40px_rgba(6,182,212,0.12)]',
      glow: 'text-cyan-400',
      badgeBg: 'bg-cyan-950/20 border-cyan-500/20 text-cyan-300',
      accentBar: 'bg-cyan-500',
      bannerText: 'STANDARD OPERATION REQUEST',
      confirmBtn: 'bg-gradient-to-r from-cyan-950 to-blue-900 border-cyan-500 text-cyan-300 hover:from-cyan-900 hover:to-blue-800'
    }
  }[currentImpact];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop blurred frame */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCancel}
          className="absolute inset-0 bg-slate-950/85 backdrop-blur-md cursor-pointer"
          id="NexoraX-confirm-dialog-backdrop"
        />

        {/* Modal Panel window */}
        <motion.div
          initial={{ scale: 0.94, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0, y: 15 }}
          transition={{ type: "spring", duration: 0.35, bounce: 0.15 }}
          className={`relative max-w-lg w-full bg-[#030712] border-2 ${theme.border} rounded-2xl overflow-hidden ${theme.shadow} text-left`}
          id="NexoraX-confirm-dialog-container"
          role="dialog"
          aria-modal="true"
        >
          {/* Top colored accent indicator line */}
          <div className={`h-1.5 w-full ${theme.accentBar}`} />

          {/* Institutional Grid pattern layout backdrop */}
          <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px] opacity-15 pointer-events-none" />

          {/* Lateral tech line accent decorations */}
          <div className="absolute top-10 left-0 w-px h-24 bg-gradient-to-b from-cyan-500/30 to-transparent" />
          <div className="absolute bottom-10 right-0 w-px h-24 bg-gradient-to-t from-[#00ff22]/20 to-transparent" />

          <div className="p-6 relative z-10">
            {/* Header / Security classification badge */}
            <div className="flex items-center justify-between gap-4 border-b border-slate-900 pb-4 mb-4">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 border text-[8px] font-mono font-black tracking-widest rounded uppercase ${theme.badgeBg}`}>
                  {theme.bannerText}
                </span>
                {options.nodeName && (
                  <span className="text-[8px] font-mono text-slate-500 tracking-wider">
                    TARGET: {options.nodeName}
                  </span>
                )}
              </div>
              
              <button 
                onClick={handleCancel}
                className="p-1 hover:bg-slate-900 border border-transparent hover:border-slate-800 rounded transition-colors text-slate-400 hover:text-white cursor-pointer"
                id="NexoraX-confirm-dialog-close-btn"
                title="Cancel interaction"
              >
                <X size={14} />
              </button>
            </div>

            {/* Core body content and visual indicators */}
            <div className="flex gap-4 items-start">
              <div className={`p-3 bg-slate-950 border border-slate-850 rounded-xl shrink-0 ${theme.glow}`}>
                {currentImpact === 'HIGH' && <ShieldAlert size={26} className="animate-pulse" />}
                {currentImpact === 'MEDIUM' && <AlertTriangle size={26} />}
                {currentImpact === 'LOW' && <HelpCircle size={26} />}
              </div>

              <div className="space-y-2 flex-1">
                <h3 className="text-sm font-sans font-black text-white uppercase tracking-tight flex items-center gap-1.5">
                  <Terminal size={14} className={theme.glow} />
                  {options.title}
                </h3>
                
                <p className="text-xs text-slate-400 font-sans leading-relaxed">
                  {options.message}
                </p>
              </div>
            </div>

            {/* System protocol metadata disclosure box */}
            <div className="mt-5 p-3.5 bg-black/45 border border-slate-900 rounded-xl font-mono text-[9px] text-slate-500 space-y-1">
              <div className="flex justify-between">
                <span>VERIFICATION SECURITY PROTOCOL:</span>
                <span className={`${theme.glow} font-bold`}>ENFORCED ACTION VERIFIED</span>
              </div>
              <div className="flex justify-between">
                <span>NEXORAX NEXORAX MAPPING:</span>
                <span>NI: Secure Credential Stored Server-Side / SC887095</span>
              </div>
              <div className="flex justify-between">
                <span>DIRECTOR DISK CAUTION SLA:</span>
                <span>0.8s Latency Buffer Active</span>
              </div>
            </div>

            {/* Interface operational control buttons */}
            <div className="flex items-center justify-end gap-3 mt-6 border-t border-slate-900 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white rounded-xl font-mono text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
                id="NexoraX-confirm-dialog-cancel-btn"
              >
                {options.cancelLabel || 'ABORT SECURELY'}
              </button>

              <button
                onClick={handleConfirm}
                className={`px-4.5 py-2 border rounded-xl font-mono text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-lg ${theme.confirmBtn}`}
                id="NexoraX-confirm-dialog-confirm-btn"
              >
                <Check size={12} />
                {options.confirmLabel || 'CONFIRM COMMAND'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
